/*
 * 上传前的文件名清洗——OSS 路径与 LLM 工具（python open(path)）一起决定了文件名
 * 只能含「相对友好」的字符。历史故障：用户上传 "模板 - 序号 56_能上传_1_份…xlsx"
 * 后，excel_edit 工具读 path 时因空格 / 中文省略号 / 多余 dash 报 ENOENT。
 *
 * 策略：
 *   - 保留中文 / 英文 / 数字 / 下划线 / 短横线
 *   - 空白 → "_"
 *   - 文件系统不友好字符（\ / : * ? " < > | …）→ "_"
 *   - 多个 "_" 合并为一个
 *   - 文件名 stem 部分截到 100 字符（OSS 限制比这宽，但 ZIP / Excel 部分工具会截）
 *   - 最后一个 "." 之后视为扩展名，单独清洗
 *   - 全部清洗完空了 → fallback 到 "file"
 */

/** 把任意文件名转换成上传/工具读取友好的版本。幂等：sanitize(sanitize(x)) === sanitize(x) */
export function sanitizeFileName(name: string): string {
  if (!name) return 'file'

  const lastDot = name.lastIndexOf('.')
  const stem = lastDot > 0 ? name.slice(0, lastDot) : name
  const ext = lastDot > 0 ? name.slice(lastDot + 1) : ''

  const safeStem = stem
    .replace(/\s+/g, '_')
    .replace(/[\\/:*?"<>|…]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_.]+|[_.]+$/g, '')
    .slice(0, 100)

  const safeExt = ext
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10)

  if (!safeStem) {
    return safeExt ? `file.${safeExt}` : 'file'
  }
  return safeExt ? `${safeStem}.${safeExt}` : safeStem
}

/**
 * 拿到上传可用的 File：如果文件名已经"干净"则原样返回（省一次 Blob 拷贝），
 * 否则用 File 构造器换一个 name（File 继承 Blob，0 数据拷贝）。
 */
export function ensureSafeFile(file: File): File {
  const safe = sanitizeFileName(file.name)
  if (safe === file.name) return file
  return new File([file], safe, {
    type: file.type,
    lastModified: file.lastModified
  })
}

/**
 * 上传前的文件名安全检查 —— 拿来给用户预先警告"这个名字会被改"，避免上传完
 * 才发现工具读不到文件 / 下载链接断。
 *
 * 返回：
 *   isSafe       — 名字够干净，不需要重命名
 *   safeName     — 清洗后的名字（无论是否需要重命名都给）
 *   unsafeChars  — 命中的字符种类（用来在弹窗里告诉用户"哪里出了问题"）
 */
export interface FileNameSafetyReport {
  isSafe: boolean
  safeName: string
  unsafeChars: string[]
}

export function checkFileNameSafety(name: string): FileNameSafetyReport {
  const safeName = sanitizeFileName(name)
  if (safeName === name) {
    return { isSafe: true, safeName, unsafeChars: [] }
  }
  const unsafeChars: string[] = []
  if (/\s/.test(name)) unsafeChars.push('空格')
  if (/[\\/]/.test(name)) unsafeChars.push('斜杠 / \\')
  if (/[*?"<>|]/.test(name)) unsafeChars.push('* ? " < > |')
  if (/…/.test(name)) unsafeChars.push('省略号 …')
  if (/:/.test(name)) unsafeChars.push('冒号 :')
  // 兜底：还有别的违规字符（控制符等）但没命中具体分类
  if (unsafeChars.length === 0) unsafeChars.push('其它非常规字符')
  return { isSafe: false, safeName, unsafeChars }
}
